import MyPDFViewer from "./PdfViewer";
import React, { useEffect, useState } from "react";
import MenuBar, { AlwaysVisible, VisibleOnHover } from "./MenuBar";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import {
  API_URL,
  API_PORT,
  depthsKey,
  descriptionsKey,
  zoomKey,
} from "./index";
import { useNavigate, useParams } from "react-router-dom";
import ProjectsDashboard from "./ProjectsDashboard";
import ProjectSondages from "./ProjectSondages";
import { getAuthHeader } from "./AuthRoute";
import { ProgressSpinner } from "primereact/progressspinner";
import DocsSwitcher from "./DocsSwitcher";
import UsersOnThisPage from "./UsersOnThisPage";

function App() {
  const navigate = useNavigate();
  const { projectName } = useParams(); // Get projectName from the URL
  const { sondageId } = useParams(); // Get sondageId from the URL
  const { docNumber } = useParams(); // Get sondageId from the URL

  function setDocNumber(nbr) {
    navigate(`/${projectName}/sondage/${sondageId}/docnbr/${nbr}`);
    // window.location.reload();
  }

  const [lastUpdated, setLastUpdated] = useState("");

  const [currentProject, setCurrentProject] = useState(null);
  const [currentSondage, setCurrentSondage] = useState(null);

  const [annotations, setAnnotations] = useState([]);

  const [interpretationFormData, setInterpretationFormData] = useState(null);

  const [isAnnotationsFetching, setIsAnnotationsFetching] = useState(false);

  const [latestTabs, setLatestTabs] = useState([]);

  function refreshLatestTabs() {
    fetch(API() + "/activity/latest-tabs", {
      headers: {
        Authorization: getAuthHeader(),
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error("Failed to fetch latest tabs");
        }
      })
      .then((data) => {
        setLatestTabs(data);
      })
      .catch((error) => {
        console.log("Error: " + (error.message || "no message"));
      });
  }

  // UI toggles saved in localstorage
  const initialDepthsVisibility =
    localStorage.getItem(depthsKey) || AlwaysVisible;
  const initialDescriptionsVisibility =
    localStorage.getItem(descriptionsKey) || VisibleOnHover;
  const [depthsVisibility, setDepthsVisibility] = useState(
    initialDepthsVisibility,
  );
  const [descriptionsVisibility, setDescriptionsVisibility] = useState(
    initialDescriptionsVisibility,
  );

  function fetchCurrentSondage(sondageId) {
    if (sondageId === undefined) {
      setCurrentSondage(null);
      return new Promise((resolve) => resolve(null));
    }
    return fetch(
      API() + "/sondages/" + sondageId + "?in_project_id=" + projectName,
      {
        headers: {
          Authorization: getAuthHeader(),
        },
      },
    )
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error("Failed to fetch sondage");
        }
      })
      .then((data) => {
        setCurrentSondage(data);
        return data;
      })
      .catch((error) => {
        alert("Error: " + (error.message || "no message"));
      });
  }

  function fetchCurrentProject(projectName) {
    if (projectName === undefined || projectName === "all-sondages") {
      setCurrentProject(null);
      return new Promise((resolve) => resolve(null));
    }
    return fetch(API() + "/projects/" + projectName, {
      headers: {
        Authorization: getAuthHeader(),
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error("Failed to fetch project");
        }
      })
      .then((data) => {
        setCurrentProject(data);
        return data;
      })
      .catch((error) => {
        alert("Error: " + (error.message || "no message"));
      });
  }

  function fetchAnnotations(sondageId, docNumber) {
    if (sondageId === undefined || docNumber === undefined) {
      return;
    }
    setIsAnnotationsFetching(true);

    function toAnnotations(data) {
      const transformedData = [];
      for (const [page, pageData] of Object.entries(data)) {
        const pageNumber = parseInt(page);
        pageData.blocks.forEach((block) => {
          transformedData.push({
            pageIndex: pageNumber,
            pageWidth: pageData.width,
            pageHeight: pageData.height,

            text: block.block_text,
            x0: block.block_x0,
            x1: block.block_x1,
            y0: block.block_y0,
            y1: block.block_y1,
            id: block.id,
            selection: block.selection ? block.selection : null,
            tagName: block.tagName ? block.tagName : undefined,
            isFirst: block.isFirst ? block.isFirst : null,
          });
        });
      }

      return transformedData;
    }

    fetch(API() + "/sondages/" + sondageId + "/" + docNumber + "/ocr", {
      headers: {
        Authorization: getAuthHeader(),
      },
    })
      .then((response) => {
        setIsAnnotationsFetching(false);
        if (response.status === 200) {
          return response;
        } else {
          throw new Error("Failed to fetch ocr code:" + response.status);
        }
      })
      .then((response) => response.json())
      .then((data) => {
        let a = labelCorrectlySelectionField(toAnnotations(data));
        setAnnotations(a);
        console.log(a);
      })
      .catch((error) => {
        alert("Error: " + (error.message || "no message"));
      });
  }

  function afterSondageFetched(currentSondage, docNumber) {
    if (!currentSondage) {
      return;
    }
    if (currentSondage.documents[docNumber].exomap != null) {
      setAnnotations(currentSondage.documents[docNumber].exomap);
    } else {
      // fetchAnnotations(sondageId, docNumber); // this will trigger a download and ocr computation in backend
    }

    if (currentSondage.interpretation != null) {
      setInterpretationFormData(currentSondage.interpretation);
      setLastUpdated(currentSondage.interpretation.updated_at ?? "");
    } else {
      const defaultInterpretation = {
        apparitionRocher: null,
        apparitionNappe: null,
        layers: [
          { app: 1, disp: 2, texture: "", squelette: "", compacite: "" },
          { app: 2, disp: null, texture: "", squelette: "", compacite: "" },
        ],
      };
      console.log("initializing default interpretation form data");
      setInterpretationFormData(defaultInterpretation);
      setLastUpdated("");
    }

    refreshLatestTabs();
  }

  function deleteOcr(sondage, docNumber) {
    fetch(API() + "/sondages/" + sondage._id + "/" + docNumber + "/ocr", {
      method: "DELETE",
      headers: {
        Authorization: getAuthHeader(),
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response;
        } else {
          throw new Error("Failed to reset ocr");
        }
      })
      .then(() => {
        alert("Reset successfully");
        window.location.reload();
      })
      .catch((error) => {
        alert("Error: " + (error.message || "no message"));
      });
  }

  useEffect(() => {
    setInterpretationFormData(null);
    refreshLatestTabs();
    fetchCurrentProject(projectName).then((currentProject) => {
      console.log(currentProject);
      fetchCurrentSondage(sondageId).then((currentSondage) => {
        console.log(currentSondage);
        afterSondageFetched(currentSondage, docNumber);
      });
    });
  }, [sondageId, docNumber]);

  function updateInterpretation(interpretation, whenSuccess = () => {}) {
    fetch(API() + "/sondages/" + sondageId + "/update-interpretation", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify({ interpretation: interpretation }),
    })
      .then((response) => {
        if (response.ok) {
          return response;
        } else {
          return response.json().then((data) => {
            throw new Error(data.error || "Failed to save interpretation");
          });
        }
      })
      .then(() => {
        whenSuccess();
      })
      .catch((error) => {
        alert("Error: " + (error.message || "no message"));
      });
  }

  function submitAnnotation() {
    let selectedAnnotations = annotations; //.filter((a) => a.selection != null);
    fetch(API() + "/sondages/" + sondageId + "/" + docNumber + "/save-exomap", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: getAuthHeader(),
      },
      body: JSON.stringify(selectedAnnotations),
    })
      .then((response) => {
        if (response.status === 200) {
          return response;
        } else {
          throw new Error("Failed to save annotations");
        }
      })
      .then(() => {
        alert("Saved successfully");
      })
      .catch((error) => {
        alert("Error: " + (error.message || "no message"));
      });
  }

  function editAnnotation(annotation) {
    annotation = { ...annotation, selection: isNaN(annotation.text) ? 1 : -1 };

    setAnnotations((prev) => {
      let prev2 = prev.map((a) => {
        if (a.id === annotation.id) {
          return annotation;
        }
        return a;
      });
      return labelCorrectlySelectionField(prev2);
    });
  }

  function selectAnnotation(annotation) {
    // if the annotation is already selected, we unselect it
    // the selection field sign allows us to know if it is a depth or a description
    // the abs value of the selection field allows us to know the order of the selection
    // a 0 or a null means that the annotation is not selected

    let isDepth = !isNaN(annotation.text); // === isANumber
    setAnnotations((prev) => {
      let prev2 = prev.map((a) => {
        if (a.id === annotation.id) {
          if (
            a.selection != null &&
            ((a.selection < 0 && isDepth) || (a.selection > 0 && !isDepth))
          ) {
            // was already selected and we want to unselect it
            return { ...a, selection: null, isFirst: true };
          }
          return { ...a, selection: isDepth ? -1 : 1, isFirst: true };
        }
        return a;
      });
      return labelCorrectlySelectionField(prev2);
    });
  }

  function deselectAnnotationsInPage(pageIndex) {
    setAnnotations((prev) => {
      let prev2 = prev.map((a) => {
        if (a.pageIndex === pageIndex) {
          return { ...a, selection: null, isFirst: true };
        }
        return a;
      });
      return labelCorrectlySelectionField(prev2);
    });
  }

  function groupAnnotation(annotation) {
    setAnnotations((prev) => {
      let prev2 = prev.map((a) => {
        if (a.id === annotation.id) {
          return { ...a, selection: 1, isFirst: false };
        }
        return a;
      });
      return labelCorrectlySelectionField(prev2);
    });
  }

  function smartSelect(annotation, unselect = false) {
    // select all rectangles that have less than 20px x0 coordinate
    function isClose(a1, a2) {
      let threshold = 20;
      if (a1.pageIndex !== a2.pageIndex) {
        return false;
      }
      return Math.abs(a1.x0 - a2.x0) < threshold;
    }

    function isUnder(a1, a2) {
      if (a1.pageIndex === a2.pageIndex) {
        return a1.y0 >= a2.y0;
      }
      return a1.pageIndex > a2.pageIndex;
    }

    setAnnotations((prev) => {
      let prev2 = prev.map((a) => {
        if (isClose(a, annotation) && isUnder(a, annotation)) {
          let isDepth = !isNaN(a.text);
          if (unselect) {
            return { ...a, selection: null, isFirst: true };
          } else {
            return { ...a, selection: isDepth ? -1 : 1, isFirst: true };
          }
        }
        return a;
      });
      return labelCorrectlySelectionField(prev2);
    });
  }

  function insertAnnotation(annotation, selected) {
    let selection = selected === null ? null : isNaN(annotation.text) ? 1 : -1;
    annotation = { ...annotation, selection: selection };
    setAnnotations((prev) => {
      return labelCorrectlySelectionField([...prev, annotation]);
    });
  }

  function labelCorrectlySelectionField(prev) {
    // i want to change the selection field of the annotation that is selected so that it is numbered in the order of y0 coordinate of the annotation when the annotation is not null and positive, and same for the negatives
    let selectedAnnotations = prev
      .filter((a) => a.tagName === undefined || a.tagName === "normal")
      .filter((a) => a.selection != null);
    let positiveAnnotations = selectedAnnotations.filter(
      (a) => a.selection > 0,
    );
    let negativeAnnotations = selectedAnnotations.filter(
      (a) => a.selection < 0,
    );

    // first sort by page index, then by y0
    positiveAnnotations.sort(
      (a, b) => a.pageIndex - b.pageIndex || a.y0 - b.y0,
    );
    negativeAnnotations.sort(
      (a, b) => a.pageIndex - b.pageIndex || a.y0 - b.y0,
    );

    let k = 1;
    for (let a of positiveAnnotations) {
      if (!a.isFirst) {
        // if alt click :
        k--;
      }
      if (k === 0) {
        // on ne veut jamais mettre un selection à 0.
        k = 1;
        a.isFirst = true;
      }
      a.selection = k; // k>=1
      console.assert(a.selection >= 1);
      k++;
    }

    k = -1;
    for (let a of negativeAnnotations) {
      a.selection = k;
      k--;
    }
    return prev;
  }

  function onConfirm() {
    if (
      interpretationFormData.status == null ||
      interpretationFormData.status === "not_done"
    ) {
      interpretationFormData.status = "done";
    } else {
      interpretationFormData.status = "not_done";
    }
    updateInterpretation(interpretationFormData, () => {
      window.location.reload();
    });

    // function checkAnnotationOk(annotations) {
    //   let selected = annotations
    //     .filter((a) => a.tagName === undefined || a.tagName === "normal")
    //     .filter((a) => a.selection != null);
    //   let positives = selected
    //     .filter((a) => a.selection > 0)
    //     .filter((a) => a.isFirst === true)
    //     .sort((a, b) => a.selection - b.selection);
    //   let negatives = selected
    //     .filter((a) => a.selection < 0)
    //     .sort((a, b) => -(a.selection - b.selection));
    //   return (
    //     positives.length === negatives.length &&
    //     positives.every((pos, i) => pos.selection === -negatives[i].selection)
    //   );
    // }
    //
    // if (checkAnnotationOk(annotations)) {
    //   submitAnnotation();
    // } else {
    //   alert(
    //     "Mismatch error!\n" +
    //       "You should have same number of descriptions, and depth values.\n" +
    //       "Please solve this and save again (maybe a depth value is not recognized as a number?).",
    //   );
    // }
  }

  function next_or_prev_sondage(direction) {
    const currentSondageId = sondageId;
    fetch(
      API() +
        "/projects/" +
        projectName +
        "/sondages/next-or-prev?current_sondage_id=" +
        currentSondageId +
        "&direction=" +
        direction,
      {
        method: "GET",
        headers: {
          Authorization: getAuthHeader(),
        },
      },
    )
      .then((response) => {
        if (response.status === 200) {
          return response;
        } else if (response.status === 404) {
          throw new Error("No " + direction + " sondage");
        } else {
          throw new Error("Failed to fetch next or prev sondage");
        }
      })
      .then((response) => response.json())
      .then((data) => {
        navigate(
          `/${projectName}/sondage/${data.sondage_id}/docnbr/${data.preferred_doc}`,
        );
      })
      .catch((error) => {
        alert("Error: " + (error.message || "no message"));
      });
  }

  const zoomPluginInstance = zoomPlugin();
  const { Zoom } = zoomPluginInstance;

  const menubarZoomCursor = (
    <div>
      {isAnnotationsFetching && (
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
      )}
      <Zoom>
        {(props) => {
          const handleZoomChange = (newScale) => {
            props.onZoom(newScale);
            localStorage.setItem(zoomKey, newScale);
          };

          return (
            <input
              type="range"
              min="0.5"
              max="5"
              step="0.1"
              value={props.scale}
              onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
              style={{
                margin: "auto",
              }}
            />
          );
        }}
      </Zoom>
    </div>
  );

  return (
    <div className="App">
      {sondageId && (
        <DocsSwitcher
          annotations={annotations}
          currentSondage={currentSondage}
          currentProject={currentProject}
          currentDocNumber={docNumber}
          setDocNumber={setDocNumber}
          deleteOcr={deleteOcr}
          formData={interpretationFormData}
          setFromData={(newData) => {
            if (typeof newData === "function") {
              newData = newData(interpretationFormData);
            }
            setInterpretationFormData(newData);
            if (newData != null) {
              updateInterpretation(newData, () => {
                setLastUpdated(new Date().toLocaleString());
              });
            }
          }}
        />
      )}
      <MenuBar
        lastUpdated={lastUpdated}
        onConfirm={() => onConfirm()}
        onPrevious={() => next_or_prev_sondage("prev")}
        onNext={() => next_or_prev_sondage("next")}
        centerItem={menubarZoomCursor}
        currentFileName={currentSondage?.sondage_name || "no name"}
        currentFileDone={
          // currentSondage?.documents[docNumber]["exomap_status"]["status"] ===
          //   "done" || false
          currentSondage?.interpretation?.status === "done" || false
        }
        latestTabs={latestTabs}
        currentFileNbrSelectedItems={
          annotations.filter(
            (a) =>
              a.selection != null ||
              (a.tagName != undefined &&
                (a.tagName == "nape" || a.tagName == "roof")),
          ).length
        }
        depthsVisibility={depthsVisibility}
        setDepthsVisibility={(e) => {
          localStorage.setItem(depthsKey, e);
          setDepthsVisibility(e);
        }}
        descriptionsVisibility={descriptionsVisibility}
        setDescriptionsVisibility={(e) => {
          localStorage.setItem(descriptionsKey, e);
          setDescriptionsVisibility(e);
        }}
      />
      {sondageId && currentSondage ? (
        <MyPDFViewer
          pdfUrl={currentSondage.documents[docNumber].pdf_url}
          fallBackPdfUrl={`${API()}/sondages/${sondageId}/${docNumber}`} // TODO remove this, it will download the pdf file... and also dont show the pdf status...
          annotations={annotations}
          editAnnotation={editAnnotation}
          selectAnnotation={selectAnnotation}
          groupAnnotation={groupAnnotation}
          smartSelect={smartSelect}
          insertAnnotation={insertAnnotation}
          deselectAnnotationsInPage={deselectAnnotationsInPage}
          zoomPluginInstance={zoomPluginInstance}
          depthsVisibility={depthsVisibility}
          descriptionsVisibility={descriptionsVisibility}
        />
      ) : (
        <div style={{ top: "70px", width: "100%", position: "relative" }}>
          {!sondageId && !projectName ? (
            <div>
              <div
                style={{
                  width: "100%",
                  position: "relative",
                  paddingLeft: "20px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <p style={{ paddingRight: "10px" }}>
                  Click to jump directly where users are:
                </p>
                {<UsersOnThisPage latestTabs={latestTabs} showTime={true} />}
              </div>
              <ProjectsDashboard />
            </div>
          ) : (
            <ProjectSondages />
          )}
        </div>
      )}
    </div>
  );
}

export function API() {
  if (API_URL !== undefined && API_URL !== "") {
    return API_URL;
  } else if (API_PORT !== undefined && API_PORT !== "") {
    let url =
      window.location.protocol +
      "//" +
      window.location.hostname +
      ":" +
      API_PORT;
    return url;
  } else {
    throw new Error("API_URL or API_PORT not defined");
  }
}

export default App;
